import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ProfilePreview } from '../../components/Profile';
import { ProfileSetupData } from '../../components/Profile/types';
import { useAuthContext } from '../../providers';
import { logger } from '../../utils/logger';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  PageDescription,
  PageContent,
  PageActions,
  ActionButton,
  BackButton,
  PageLoading,
  LoadingSpinner,
  LoadingText,
  PageError,
  ErrorMessage,
  ErrorAction
} from '../../styles/Profile/ProfilePages.styles';

export const ProfilePreviewPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, isAuthenticated } = useAuthContext();
  const [profileData, setProfileData] = useState<ProfileSetupData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Redirect if not authenticated
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  const fetchProfileData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Check if profile data was passed via navigation state
      if (location.state?.profileData) {
        setProfileData(location.state.profileData);
        setIsLoading(false);
        return;
      }
      
      // Simulate API call to fetch profile data
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Mock profile data
      const mockProfileData: ProfileSetupData = {
        basicInfo: {
          firstName: user?.user_metadata?.full_name?.split(' ')[0] || 'John',
          lastName: user?.user_metadata?.full_name?.split(' ')[1] || 'Doe',
          dateOfBirth: new Date('1990-01-01'),
          gender: 'other',
          location: {
            city: 'New York',
            state: 'NY',
            country: 'US',
            coordinates: {
              lat: 40.7128,
              lng: -74.0060
            }
          },
          occupation: 'Software Engineer',
          education: 'Bachelor\'s Degree'
        },
        photos: [
          {
            id: '1',
            url: '/api/placeholder/300/400',
            order: 0,
            isMain: true,
            uploadedAt: new Date(),
            isValidated: true,
            validationStatus: 'approved'
          },
          {
            id: '2',
            url: '/api/placeholder/300/400',
            order: 1,
            isMain: false,
            uploadedAt: new Date(),
            isValidated: true,
            validationStatus: 'approved'
          },
          {
            id: '3',
            url: '/api/placeholder/300/400',
            order: 2,
            isMain: false,
            uploadedAt: new Date(),
            isValidated: false,
            validationStatus: 'pending'
          }
        ],
        bio: {
          content: 'I love exploring new places, trying different cuisines, and meeting interesting people. Looking for someone to share adventures with!',
          characterCount: 120,
          maxCharacters: 500,
          lastUpdated: new Date()
        },
        interests: [
          { id: '1', name: 'Photography', category: 'hobbies', icon: '📸' },
          { id: '2', name: 'Travel', category: 'travel', icon: '✈️' },
          { id: '3', name: 'Cooking', category: 'food', icon: '👨‍🍳' },
          { id: '4', name: 'Music', category: 'music', icon: '🎵' },
          { id: '5', name: 'Fitness', category: 'sports', icon: '💪' },
          { id: '6', name: 'Reading', category: 'books', icon: '📚' }
        ],
        preferences: {
          ageRange: { min: 25, max: 35 },
          distance: 20,
          genderPreference: ['female'],
          interestedIn: ['dating', 'serious'],
          dealBreakers: ['Smoking', 'Heavy drinking']
        },
        currentStep: 5,
        isComplete: true,
        verificationStatus: 'verified'
      };
      
      setProfileData(mockProfileData);
    } catch (error) {
      console.error('Failed to fetch profile data:', error);
      setError('Failed to load profile data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchProfileData();
    }
  }, [isAuthenticated]);

  const handleEdit = () => {
    navigate('/profile-editor');
  };

  const handlePublish = async () => {
    try {
      // Simulate API call to publish profile
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      logger.log('Profile published:', profileData);
      
      // Show success message and redirect
      alert('Profile published successfully! You can now start connecting with others.');
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to publish profile:', error);
      alert('Failed to publish profile. Please try again.');
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: 'My Profile',
        text: 'Check out my profile on Plenty of Hearts!',
        url: window.location.href
      });
    } else {
      // Fallback to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Profile link copied to clipboard!');
    }
  };

  const handleBack = () => {
    navigate('/dashboard');
  };

  if (loading || isLoading) {
    return (
      <PageContainer>
        <PageLoading>
          <LoadingSpinner />
          <LoadingText>Loading profile preview...</LoadingText>
        </PageLoading>
      </PageContainer>
    );
  }

  if (!isAuthenticated) {
    return (
      <PageContainer>
        <PageError>
          <ErrorMessage>Please log in to access profile preview.</ErrorMessage>
          <ErrorAction onClick={() => navigate('/login')}>
            Go to Login
          </ErrorAction>
        </PageError>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <PageHeader>
          <PageTitle>Profile Preview</PageTitle>
          <PageDescription>
            Review your profile before publishing it to the world.
          </PageDescription>
        </PageHeader>
        
        <PageError>
          <ErrorMessage>{error}</ErrorMessage>
          <ErrorAction onClick={fetchProfileData}>
            Try Again
          </ErrorAction>
        </PageError>
      </PageContainer>
    );
  }

  if (!profileData) {
    return (
      <PageContainer>
        <PageError>
          <ErrorMessage>No profile data found. Please complete your profile setup first.</ErrorMessage>
          <ErrorAction onClick={() => navigate('/profile-setup')}>
            Go to Profile Setup
          </ErrorAction>
        </PageError>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Profile Preview</PageTitle>
        <PageDescription>
          Review your profile before publishing it to the world. Make sure everything looks perfect!
        </PageDescription>
      </PageHeader>

      <PageContent>
        <ProfilePreview
          profileData={profileData}
          onEdit={handleEdit}
          onPublish={handlePublish}
          onShare={handleShare}
        />
      </PageContent>

      <PageActions>
        <BackButton onClick={handleBack}>
          ← Back to Dashboard
        </BackButton>
      </PageActions>
    </PageContainer>
  );
};
